import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { callAPIAdmin } from '../../utils/apiUtils';
import { apiUrls } from '../../utils/apiUrls';

export const addDriver = createAsyncThunk(
  'driver/add',
  async (driverData, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminCreateDriver, {}, 'POST', driverData);
      if (response.data.success) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to add driver');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const updateDriver = createAsyncThunk(
  'driver/update',
  async ({ id, driverData }, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(`${apiUrls.adminUpdateDriver}/${id}`, {}, 'PATCH', driverData);
      if (response.data.success) {
        return response.data.data;
      }
      return rejectWithValue(response.data.message || 'Failed to update driver');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const deleteDriver = createAsyncThunk(
  'driver/delete',
  async (id, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminDeleteDrivers, { _id: id }, 'DELETE', {});
      if (response.data.success) {
        return { id, message: response.data.message };
      }
      return rejectWithValue(response.data.message || 'Failed to delete driver');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

export const updateDriverStatus = createAsyncThunk(
  'driver/updateStatus',
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const response = await callAPIAdmin(apiUrls.adminDriverStatusUpdate, { _id: id, status }, 'PATCH', {});
      if (response.data.success) {
        return { id, status, message: response.data.message };
      }
      return rejectWithValue(response.data.message || 'Failed to update status');
    } catch (error) {
      return rejectWithValue(error?.response?.data?.message || error?.message);
    }
  }
);

const initialState = {
  mutationLoading: false,
  mutationError: null,
  mutationSuccess: null,
};

const adminDriverSlice = createSlice({
  name: 'driver',
  initialState,
  reducers: {
    clearMutationError: (state) => {
      state.mutationError = null;
    },
    clearMutationSuccess: (state) => {
      state.mutationSuccess = null;
    },
    resetMutationState: (state) => {
      state.mutationLoading = false;
      state.mutationError = null;
      state.mutationSuccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(addDriver.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(addDriver.fulfilled, (state) => {
        state.mutationLoading = false;
        state.mutationSuccess = 'Driver added successfully';
      })
      .addCase(addDriver.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      })
      .addCase(updateDriver.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(updateDriver.fulfilled, (state) => {
        state.mutationLoading = false;
        state.mutationSuccess = 'Driver updated successfully';
      })
      .addCase(updateDriver.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      })
      .addCase(deleteDriver.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(deleteDriver.fulfilled, (state) => {
        state.mutationLoading = false;
        state.mutationSuccess = 'Driver deleted successfully';
      })
      .addCase(deleteDriver.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      })
      .addCase(updateDriverStatus.pending, (state) => {
        state.mutationLoading = true;
        state.mutationError = null;
        state.mutationSuccess = null;
      })
      .addCase(updateDriverStatus.fulfilled, (state, action) => {
        state.mutationLoading = false;
        state.mutationSuccess = `Status changed to ${action.payload?.status}`;
      })
      .addCase(updateDriverStatus.rejected, (state, action) => {
        state.mutationLoading = false;
        state.mutationError = action.payload;
      });
  },
});

export const { clearMutationError, clearMutationSuccess, resetMutationState } = adminDriverSlice.actions;
export default adminDriverSlice.reducer;